import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { Vimeo } from "@swizec/gatsby-theme-course-platform";
export const title = "Module 2";
export const description = "";
export const image = "/chapter_headers/serverless-elements.png";
export const _frontmatter = {};
const layoutProps = {
  title,
  description,
  image,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <h1 {...{
      "id": "module-2-setting-up-your-app"
    }}>{`Module 2: Setting up your app`}</h1>
    <p>{`In `}<a parentName="p" {...{
        "href": "/module-2"
      }}>{`Module 2`}</a>{` you will setup your project to use the modern stack. Create a new webapp, set up a design system with theming support, configure a basic GraphQL server on AWS Lambda, and add authentication to your app.`}</p>
    <p>{`You can find code for this module `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/markdownlandingpage.com"
      }}>{`on GitHub`}</a>{`. Commits follow the flow of these videos.`}</p>
    <h2 {...{
      "id": "outline-your-project"
    }}>{`Outline your project`}</h2>
    <Vimeo id="390025846" mdxType="Vimeo" />
    <h2 {...{
      "id": "create-your-repository"
    }}>{`Create your repository`}</h2>
    <Vimeo id="390178149" mdxType="Vimeo" />
    <h2 {...{
      "id": "create-gatsby-webapp"
    }}>{`Create Gatsby webapp`}</h2>
    <p>{`You will need to install Gatsby for this step.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`npm install -g gatsby-cli
`}</code></pre>
    <Vimeo id="390182900" mdxType="Vimeo" />
    <h3 {...{
      "id": "update-config"
    }}>{`Update config`}</h3>
    <p>{`See how Gatsby uses GraphQL to read local config files.`}</p>
    <Vimeo id="390218902" mdxType="Vimeo" />
    <h2 {...{
      "id": "configure-styling-with-rebass"
    }}>{`Configure styling with Rebass`}</h2>
    <Vimeo id="390235387" mdxType="Vimeo" />
    <h3 {...{
      "id": "gatsby-component-shadowing"
    }}>{`Gatsby component shadowing`}</h3>
    <p>{`We use component shadowing to override the default theme config for our site. You can use this technique to change any files coming from a plugin.`}</p>
    <Vimeo id="390235516" mdxType="Vimeo" />
    <h3 {...{
      "id": "rebass-responsiveness"
    }}>{`Rebass responsiveness`}</h3>
    <Vimeo id="390364835" mdxType="Vimeo" />
    <h2 {...{
      "id": "basic-landing-page"
    }}>{`Basic landing page`}</h2>
    <Vimeo id="390364642" mdxType="Vimeo" />
    <h2 {...{
      "id": "authentication"
    }}>{`Authentication`}</h2>
    <p>{`Authentication works via Auth0 using the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth`}</a>{` hook.`}</p>
    <Vimeo id="390379219" mdxType="Vimeo" />
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/79904e89de3492409b1ad433e94edcce/0f882/auth0_flow.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "81.61434977578475%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'326\\'%20viewBox=\\'0%200%20400%20326\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M175%206c-44%206-90%2032-104%2059l-6%2012-2%205-2-3-1-4c2%200%201-2-1-2-3%200-2%203%202%208%204%206%206%206%2011-1l4-6c0-2-2-1-5%205-8%2013-6%201%203-15%208-15%2033-34%2059-44s44-13%2076-13c23%201%2023%201%2055%209%2029%208%2062%2024%2075%2037%209%2010%2012%2017%209%2025-2%205-1%207%202%201%204-11-5-26-23-37-7-5-33-19-42-22l-19-5-24-7c-9-2-11-2-35-3l-32%201m191%2081l-32%203-20%201-1%2019a2071%202071%200%2001-2%2071c1%202%2020%202%2044%200l19-2c11%200%2011%200%2012-25a477%20477%200%20016-68c0-2-14-1-26%201m5%201l-32%203c-27%200-24-2-24%2017%200%2016-1%2043-3%2064v9h15a232%20232%200%200041-3c16-1%2015%201%2016-22a647%20647%200%20017-69c-1-1-8-1-20%201M15%2088l17%201a419%20419%200%200149%203h15v13a479%20479%200%2001-4%2059c0%203-1%204-6%207-3%203-5%204-5%203l-5-3c-6-1-42%200-50%201l-6%201v5l-1%208v4h15a376%20376%200%200041%201h6v-7c0-7%201-8%208-13%203-1%203-1%203%201l-1%2012c0%208%200%2010-1%209l-77%203h-3l1-10-1-13a482%20482%200%2001-3-15%204421%204421%200%20015%2039l78-2%204%201-1-13v-14l11-8a2102%202102%200%200033-20c-2%201-2%202-2%203l3-3%206-7c6-7%205-10-3-6l-9%206-3%203h4l4-1-3%202a1168%201168%200%2000-32%2023l-8%205v-3l2-26c2-26%203-44%201-46l-16-1-23-1c-12-1-35-2-43-1m147%200l-8%202%205%201c23-3%2029-3%2048-3%2023%201%2030%202%2032%206l1%2052v49h-14l-28-2-31-1h-17v-2l2-7%201-6v-14a1011%201011%200%2001-3-29c1%2041%200%2054-2%2054l-1%202c0%204%200%204%2030%204a434%20434%200%200151%203h14v-50c0-56%200-55-6-58-4-2-60-2-74-1M29%2099h-6c-4%200-5%200-8%203l-3%204%203-2c2-2%203-2%2020-1%2016%200%2020%200%2017-3-2-1-21-2-23-1m41%203c-2%200-3%201-4%204l-1%2015%201-8c1-9%202-10%209-10%2010%200%2010%200%209%2012v11l-4-1c-3%200-4-1-4-3%200-3-3-5-5-4s-3%203-1%203l1-1%202-1%201%203c0%203%200%203-4%203l-5-1h-2l2%201%2020%202%201-11%201-11-4-2c-3-2-7-2-13-1m96%2018c-5%200-6%201-6%202l-5%2021c1%203%201%203%2020%203h26l18-1h11v-9c0-10-1-12-6-10l-5-1a410%20410%200%2000-53-5m-1%202h-3l-3%2010-2%2011c0%202%2034%203%2047%200l16-1%208%201v-7c1-8%201-9-2-9h-8c-11-3-46-6-53-5M28%20135l-4%203c0%202%203%202%205%200%204-3%205-3%207%200%203%205%206%206%2011%203%203-1%206-2%207-1%202%201%206-1%206-3%200-3-8-4-13-1-3%202-5%202-8-2-3-3-5-2-11%201m130%2019l-1%209-1%208c-2%202-1%203%201%203%2051%202%2066%202%2066-1h-2c-2%201-54%200-60-1h-3l1-7c1-11-3-10%2028-10l34%201h7l-1%203-1%2015%202-7%202-10v-3h-10a1615%201615%200%2001-62%200M48%20172c-27%201-26%201-27%2010l-1%206h14l29%201%2016%201v-5c1-7%201-7-2-5s-4%201%200-2l3-3-12-3H48m235%2028a111178%20111178%200%2001-56%2034l4-6c-1-1-7%205-10%2010s-1%206%205%202l8-3%202-1-2-1%2040-27c12-7%2018-12%2017-12l-8%204m-79%200c-3%201-9%207-8%208l3-2c2-1%202-1%202%201-3%2011-5%2024-3%2024l3-14c2-15%203-18%204-11s3%207%202%200c0-6-1-7-3-6m89%202a635%20635%200%2001-30%2027%201072%201072%200%200133-23c-3%206-3%208-2%208l2-4%203-6c3-5%202-5-6-2m-239%202l-5%205c-1%202-2%203-3%202-2-1-2-1-1-2%202-1-2-5-5-5-2%200-2%200-1%201v5l1%204v-2l1-4%202-1v1c-2%201-1%203%201%204%203%200%203%201-1%203l-2%201h2l5-2c3-4%207-4%209-1l2%203-1-3-1-6c0-4-1-4-3-3m41%2019l3%202%208%204a603%20603%200%200047%2030c-5-2-10-2-9-1l13%203-1-5-1-6v-1l-1%203c0%204%200%204-9-2a689%20689%200%2001-50-27m-19%2012l1%206%201-2v-3l30%2014%2020%2011%2015%208c7%206%208%205%202%200-3-3-10-7-15-9l-19-10-17-9-6-5-4-3-4-3c-3-3-4-1-4%205m99%200l-1%202-2%201c-4%200-5%203-5%2014l-2%2018c0%208%200%2012%203%2013l6%203c8%204%2019%202%2028-6l4-2%201%201%201-3%202-11%202-16%201-8c2-2-2-5-5-5-5-1-31-2-33-1m1%202v1l3%201-3%201c-4-1-8%201-8%204%201%205%2042%202%2044-4%200-2-4-3-20-3h-16m31%209l-22%201-17%201v20l-1%2012%204%203c9%206%2021%204%2031-4l5-5%202-12%202-15v-3l-4%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
        `}<source parentName="picture" {...{
                "srcSet": ["/static/79904e89de3492409b1ad433e94edcce/ca0a1/auth0_flow.webp 223w", "/static/79904e89de3492409b1ad433e94edcce/75680/auth0_flow.webp 445w", "/static/79904e89de3492409b1ad433e94edcce/8d1ba/auth0_flow.webp 890w", "/static/79904e89de3492409b1ad433e94edcce/3838e/auth0_flow.webp 1335w", "/static/79904e89de3492409b1ad433e94edcce/8754d/auth0_flow.webp 1584w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
        `}<source parentName="picture" {...{
                "srcSet": ["/static/79904e89de3492409b1ad433e94edcce/e92b6/auth0_flow.png 223w", "/static/79904e89de3492409b1ad433e94edcce/e66bf/auth0_flow.png 445w", "/static/79904e89de3492409b1ad433e94edcce/4ef49/auth0_flow.png 890w", "/static/79904e89de3492409b1ad433e94edcce/4e814/auth0_flow.png 1335w", "/static/79904e89de3492409b1ad433e94edcce/0f882/auth0_flow.png 1584w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
        `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/79904e89de3492409b1ad433e94edcce/4ef49/auth0_flow.png",
                "alt": "Authentication flow",
                "title": "Authentication flow",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
      `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Authentication flow`}</figcaption>{`
  `}</figure></p>
    <h3 {...{
      "id": "add-useauth-hook"
    }}>{`Add useAuth hook`}</h3>
    <Vimeo id="390386823" mdxType="Vimeo" />
    <Vimeo id="390393362" mdxType="Vimeo" />
    <h2 {...{
      "id": "the-backend"
    }}>{`The backend`}</h2>
    <p>{`The backend works with AWS Lambda using Serverless Framework for configuration.`}</p>
    <p>{`I recommend you `}<a parentName="p" {...{
        "href": "https://serverless.com/framework/docs/providers/aws/guide/credentials/"
      }}>{`follow their documentation to setup your local environment and AWS credentials`}</a>{`. AWS will ask for your creit card, if they don't have it yet.`}</p>
    <p>{`Don't worry, it's still free until you have quite a bit of traffic :)`}</p>
    <Vimeo id="390408047" mdxType="Vimeo" />
    <h3 {...{
      "id": "serverlessyml"
    }}>{`serverless.yml`}</h3>
    <p>{`This is where you configure your whole infrastructure.`}</p>
    <Vimeo id="390408266" mdxType="Vimeo" />
    <h3 {...{
      "id": "graphql-lambda"
    }}>{`GraphQL Lambda`}</h3>
    <Vimeo id="390408529" mdxType="Vimeo" />
    <h3 {...{
      "id": "deploy-script"
    }}>{`Deploy script`}</h3>
    <p>{`This goes in your package.json and makes life easier :)`}</p>
    <Vimeo id="390408756" mdxType="Vimeo" />
    <p>{`You might need to install TypeScript globally, if you don't have it yet.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`npm install -g typescript ts-node
`}</code></pre>
    <p>{`And serverless too`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`npm install -g serverless
`}</code></pre>
    <h3 {...{
      "id": "what-is-the-server-anyway"
    }}>{`What is The Server anyway?`}</h3>
    <Vimeo id="390408835" mdxType="Vimeo" />
    <h2 {...{
      "id": "connect-your-webapp-to-graphql"
    }}>{`Connect your webapp to GraphQL`}</h2>
    <Vimeo id="390523141" mdxType="Vimeo" />
    <h3 {...{
      "id": "use-static-query"
    }}>{`use static query`}</h3>
    <p>{`Queries specifically for static data fetched dynamically at build.`}</p>
    <Vimeo id="390522624" mdxType="Vimeo" />
    <h3 {...{
      "id": "connect-to-graphql-at-run-time"
    }}>{`Connect to GraphQL at run-time`}</h3>
    <Vimeo id="390522225" mdxType="Vimeo" />
    <h3 {...{
      "id": "use-live-data"
    }}>{`Use live data`}</h3>
    <Vimeo id="390521693" mdxType="Vimeo" />
    <h2 {...{
      "id": "what-we-created"
    }}>{`What we created`}</h2>
    <p>{`The basics are there, the patterns were experimented with. Next module we build something useful, save actual data, and have heaps of fun.`}</p>
    <Vimeo id="390523333" mdxType="Vimeo" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      